<template>
  <div>
    <b-alert
      v-if="showMasterTeamOnlyWarning"
      show
      variant="warning"
      class="p-1 text-center alert-on-gray-bg status-change-master-warning"
    >
      {{ masterTeamOnlyWarningMessage }}
    </b-alert>

    <b-row
      v-else-if="Object.keys(list).length"
      class="match-height mb-2"
    >
      <b-col
        v-for="(status) in list"
        v-show="status.canBeNext"
        :key="status.value"
      >
        <b-button
          :variant="status.color"
          :disabled="loading"
          class="btn-block p-1 font-medium-3 h-100"
          @click="handleStatusAction(status.value)"
        >
          {{ status.label }}
        </b-button>
      </b-col>
    </b-row>

    <b-modal
      id="modal-status-change-reason"
      color="danger"
      :title="$t(`popups.order-status.comment-required.${reasonModalType}.title`)"
      :ok-title="$t('shared.button.submit')"
      :cancel-title="$t('shared.button.cancel')"
      :ok-disabled="trimmedReason.length === 0"
      @hidden="resetReasonModal"
      @ok="submitReasonedStatusChange"
    >
      <form @submit.prevent>
        <label>
          {{ $t(`popups.order-status.comment-required.${reasonModalType}.description`) }}
        </label>
        <b-form-input
          v-model="reason"
          :placeholder="$t(`popups.order-status.comment-required.${reasonModalType}.comment-label`)"
          required
        />
      </form>
    </b-modal>
  </div>
</template>
<script>
import { sprintf } from 'printj'
import { parseRequestError } from '@/helpers/helpers'
import orderTypes from '@/dictionaries/orderTypes'
import { isPatientVisibleInView, patientTransportStatuses, resolvePatientTransportStatus } from '@/helpers/patient'
import { withTenantKey } from '@/libs/storageKeys'

const STATUS_CHANGE_WITHOUT_ASSIGNED_TEAM_WARNING_KEY = 'orderStatusChangeWithoutAssignedTeamWarningShownAt'
const DEFAULT_WARNING_THROTTLE_SECONDS = 300

export default {
  props: {
    toggleShow: {
      type: Function,
      required: true,
    },
    order: {
      type: Object,
      required: true,
    },
    workflow: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      reason: '',
      reasonModalType: 'default',
      pendingStatus: null,
      optimisticHandledPatientIds: {
        patient_pick_up: [],
        patient_delivery: [],
        patient_not_taken: [],
        patient_left_at_destination: [],
      },
    }
  },
  computed: {
    patients() {
      const patients = Array.isArray(this.order.patientInfo) ? this.order.patientInfo : []
      return patients.filter(patient => isPatientVisibleInView(patient))
    },
    availableTransitions() {
      if (!Array.isArray(this.workflow?.availableStatusTransitions)) {
        return []
      }

      return this.workflow.availableStatusTransitions.filter(transition => transition && transition.allowed)
    },
    list() {
      return this.availableTransitions.reduce((accumulator, transition) => ({
        ...accumulator,
        [transition.targetStatus]: this.getStatusData(transition.targetStatus),
      }), {})
    },
    isMultiTransport() {
      return this.order && this.order.type === orderTypes.multi_transport
    },
    normalizedMultiTransportDirection() {
      return this.resolveMultiTransportDirection(this.order?.multiTransportDirection)
    },
    trimmedReason() {
      return typeof this.reason === 'string' ? this.reason.trim() : ''
    },
    requestVersion() {
      const orderVersion = Number(this.order?.version)

      return Number.isFinite(orderVersion) ? orderVersion : null
    },
    showMasterTeamOnlyWarning() {
      return this.workflow?.context?.statusChangeRestrictedToMasterTeam === true
    },
    masterTeamOnlyWarningMessage() {
      return sprintf(
        this.$t('order.workflow.master-team-only-status-change'),
        this.workflow?.context?.masterTeamCode || '',
      )
    },
    statusChangeWithoutAssignedTeamWarning() {
      return this.workflow?.context?.statusChangeWithoutAssignedTeamWarning || null
    },
  },
  watch: {
    order: {
      handler(newOrder, oldOrder) {
        if (this.loading && newOrder.updatedAt !== oldOrder.updatedAt) {
          this.loading = false
        }
        if (newOrder.updatedAt !== oldOrder.updatedAt) {
          this.optimisticHandledPatientIds = {
            patient_pick_up: [],
            patient_delivery: [],
            patient_not_taken: [],
            patient_left_at_destination: [],
          }
        }
      },
      deep: true,
    },
  },
  methods: {
    sprintf,
    resolveMultiTransportDirection(direction) {
      if (!direction) {
        return null
      }

      if (typeof direction === 'string') {
        const normalized = direction.toLowerCase()
        return normalized === 'inbound' || normalized === 'outbound' ? normalized : null
      }

      if (typeof direction === 'object') {
        const possible = [
          direction.value,
          direction.key,
          direction.code,
          direction.name,
          direction.direction,
          direction.id,
        ]
        for (let index = 0; index < possible.length; index += 1) {
          const resolved = this.resolveMultiTransportDirection(possible[index])
          if (resolved) {
            return resolved
          }
        }
      }

      return null
    },
    getNextPatientForPickup() {
      // Pick next patient that is still pending.
      const nextNotOnBoard = this.patients.find(patient => (
        patient
        && patient.id
        && resolvePatientTransportStatus(patient) === patientTransportStatuses.pending
        && !this.isPatientOptimisticallyHandled('patient_pick_up', patient.id)
      ))

      if (nextNotOnBoard) {
        return nextNotOnBoard
      }

      // Before pusher refresh arrives, fall back to next patient in order list.
      return this.getNextPatientByOrder('patient_pick_up')
    },
    getOptimisticHandledPatientIds(status) {
      return Array.isArray(this.optimisticHandledPatientIds[status])
        ? this.optimisticHandledPatientIds[status]
        : []
    },
    isPatientOptimisticallyHandled(status, patientId) {
      return this.getOptimisticHandledPatientIds(status)
        .some(id => String(id) === String(patientId))
    },
    addOptimisticHandledPatient(status, patientId) {
      if (!patientId) {
        return
      }
      if (this.isPatientOptimisticallyHandled(status, patientId)) {
        return
      }
      this.optimisticHandledPatientIds = {
        ...this.optimisticHandledPatientIds,
        [status]: [...this.getOptimisticHandledPatientIds(status), patientId],
      }
    },
    removeOptimisticHandledPatient(status, patientId) {
      if (!patientId) {
        return
      }
      this.optimisticHandledPatientIds = {
        ...this.optimisticHandledPatientIds,
        [status]: this.getOptimisticHandledPatientIds(status).filter(id => String(id) !== String(patientId)),
      }
    },
    getNextPatientByOrder(status) {
      return this.patients.find(patient => (
        patient
        && patient.id
        && !this.isPatientOptimisticallyHandled(status, patient.id)
      ))
    },
    getNextPatientForDelivery() {
      // Delivery targets one onboard patient at a time.
      const notDeliveredAndOnBoard = this.patients.find(patient => (
        patient
        && patient.id
        && resolvePatientTransportStatus(patient) === patientTransportStatuses.onBoard
        && !this.isPatientOptimisticallyHandled('patient_delivery', patient.id)
      ))

      if (notDeliveredAndOnBoard) {
        return notDeliveredAndOnBoard
      }

      // Before pusher refresh arrives, fall back to next patient in order list.
      return this.getNextPatientByOrder('patient_delivery')
    },
    getNextPatientForNotTaken() {
      const nextPendingPatient = this.patients.find(patient => (
        patient
        && patient.id
        && resolvePatientTransportStatus(patient) === patientTransportStatuses.pending
        && !this.isPatientOptimisticallyHandled('patient_not_taken', patient.id)
      ))

      if (nextPendingPatient) {
        return nextPendingPatient
      }

      return this.getNextPatientByOrder('patient_not_taken')
    },
    resolvePatientIdForStatus(newStatus) {
      if (!this.isMultiTransport) {
        return null
      }

      if (newStatus === 'patient_pick_up') {
        if (this.normalizedMultiTransportDirection === 'outbound') {
          return null
        }
        return this.getNextPatientForPickup()?.id || null
      }

      if (newStatus === 'patient_delivery') {
        if (this.normalizedMultiTransportDirection === 'inbound') {
          return null
        }
        return this.getNextPatientForDelivery()?.id || null
      }

      if (newStatus === 'patient_not_taken') {
        return this.getNextPatientForNotTaken()?.id || null
      }

      return null
    },
    getStatusData(status) {
      const current = this.order.status
      const transition = this.findTransition(status)

      return {
        value: status,
        label: this.resolveStatusLabel(status, transition),
        isCurrent: current === status,
        canBeNext: !!transition?.allowed,
        color: this.selectColor(status),
      }
    },
    findTransition(status) {
      return this.availableTransitions.find(transition => transition.targetStatus === status) || null
    },
    isBulkStatusAction(status) {
      if (!this.isMultiTransport) {
        return false
      }

      if (this.normalizedMultiTransportDirection === 'outbound' && status === 'patient_pick_up') {
        return true
      }

      if (this.normalizedMultiTransportDirection === 'inbound' && status === 'patient_delivery') {
        return true
      }

      return false
    },
    resolveStatusLabel(status, transition = null) {
      const pluralKey = `order.status.${status}-description-plural`
      const singularKey = `order.status.${status}-description`
      const labelKey = transition?.labelKey || singularKey

      if (this.isBulkStatusAction(status) && this.$te(pluralKey)) {
        return this.$t(pluralKey)
      }

      if (this.$te(labelKey)) {
        return this.$t(labelKey)
      }

      return this.$t(singularKey)
    },
    selectColor(status) {
      if (['cancelled'].includes(status)) {
        return 'danger'
      }

      if (['patient_not_taken', 'patient_left_at_destination'].includes(status)) {
        return 'warning'
      }

      return 'success'
    },
    async handleStatusAction(newStatus) {
      const transition = this.findTransition(newStatus)

      if (this.requiresReason(transition)) {
        this.pendingStatus = newStatus
        this.reason = ''
        this.reasonModalType = this.resolveReasonModalType(transition)
        this.$bvModal?.show?.('modal-status-change-reason')

        return
      }

      await this.updateStatus(newStatus)
    },
    async confirmStatusChangeWithoutAssignedTeamWarning(newStatus) {
      const warning = this.statusChangeWithoutAssignedTeamWarning
      if (
        !warning?.enabled
        || !this.shouldConfirmStatusChangeWithoutAssignedTeamWarning(warning, newStatus)
        || !this.canShowStatusChangeWithoutAssignedTeamWarning()
      ) {
        return true
      }

      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.resolveStatusChangeWithoutAssignedTeamWarningMessage(warning),
        {
          title: this.$t('order.workflow.status_change_without_assigned_team_confirm_title'),
          okTitle: this.$t('shared.yes'),
          cancelTitle: this.$t('shared.no'),
          okVariant: 'warning',
          cancelVariant: 'outline-secondary',
        },
      )

      if (!confirmed) {
        return false
      }

      localStorage.setItem(this.statusChangeWithoutAssignedTeamWarningStorageKey(), String(Date.now()))

      return true
    },
    shouldConfirmStatusChangeWithoutAssignedTeamWarning(warning, newStatus) {
      if (!Array.isArray(warning?.targetStatuses)) {
        return true
      }

      return warning.targetStatuses.includes(newStatus)
    },
    canShowStatusChangeWithoutAssignedTeamWarning() {
      const rawLastShownAt = localStorage.getItem(this.statusChangeWithoutAssignedTeamWarningStorageKey())
      const lastShownAt = Number(rawLastShownAt)
      if (!Number.isFinite(lastShownAt)) {
        return true
      }

      return Date.now() - lastShownAt >= this.statusChangeWithoutAssignedTeamWarningThrottleMs()
    },
    statusChangeWithoutAssignedTeamWarningThrottleMs() {
      const throttleSeconds = Number(this.statusChangeWithoutAssignedTeamWarning?.throttleSeconds)

      return (Number.isFinite(throttleSeconds) && throttleSeconds > 0
        ? throttleSeconds
        : DEFAULT_WARNING_THROTTLE_SECONDS) * 1000
    },
    resolveStatusChangeWithoutAssignedTeamWarningMessage(warning) {
      const messageKey = warning?.messageKey || 'order.workflow.status_change_without_assigned_team_warning'

      return this.$t(messageKey)
    },
    statusChangeWithoutAssignedTeamWarningStorageKey() {
      return withTenantKey(STATUS_CHANGE_WITHOUT_ASSIGNED_TEAM_WARNING_KEY)
    },
    async updateStatus(newStatus, reason = null) {
      if (this.loading) {
        return
      }

      if (!this.requestVersion) {
        parseRequestError({ message: this.$t('shared.loading') })
        return
      }

      const confirmed = await this.confirmStatusChangeWithoutAssignedTeamWarning(newStatus)
      if (!confirmed) {
        return
      }

      this.loading = true
      const statusData = {
        status: newStatus,
      }

      const patientInfoId = this.resolvePatientIdForStatus(newStatus)
      const requiresPatientSelection = this.isMultiTransport && (
        ['patient_not_taken'].includes(newStatus)
        || (newStatus === 'patient_delivery' && this.normalizedMultiTransportDirection !== 'inbound')
        || (newStatus === 'patient_pick_up' && this.normalizedMultiTransportDirection !== 'outbound')
      )

      if (requiresPatientSelection && !patientInfoId) {
        this.loading = false
        window.toast.notify.warning(this.$t('order.patient-info.select'))
        return
      }
      if (patientInfoId) {
        statusData.patientInfoId = patientInfoId
        this.addOptimisticHandledPatient(newStatus, patientInfoId)
      }

      if (this.patients.length && newStatus === 'patient_pick_up') {
        statusData.patientOnBoard = true
      }

      if (this.patients.length && newStatus === 'patient_delivery') {
        statusData.patientOnBoard = false
      }

      if (this.patients.length && newStatus === 'patient_not_taken') {
        statusData.patientOnBoard = false
      }

      if (this.patients.length && newStatus === 'patient_left_at_destination') {
        statusData.patientOnBoard = false
      }

      if (reason) {
        statusData.reason = reason
      }

      this.$getLocation()
        .then(coordinates => {
          if (Number.isFinite(coordinates?.lat) && Number.isFinite(coordinates?.lng)) {
            statusData.latitude = coordinates.lat
            statusData.longitude = coordinates.lng
          }
        })
        .catch(error => {
          const message = typeof error === 'string' ? error : error?.message
          if (message) {
            // eslint-disable-next-line no-console
            console.error(`Status change process: ${message}`)
          }
        })
        .finally(() => {
          const payload = {
            id: this.order.id,
            data: {
              ...statusData,
              version: this.requestVersion,
            },
          }
          this.$store.dispatch('order/updateOrderStatus', payload)
            .then(() => {
              this.$emit('status-change-response')
              window.toast.notify.success(this.$t('order.notify.edit.success_title'))
              this.toggleShow()
            })
            .catch(err => {
              if (patientInfoId) {
                this.removeOptimisticHandledPatient(newStatus, patientInfoId)
              }
              parseRequestError(err)
            })
            .finally(() => {
              this.loading = false
            })
        })
    },
    submitReasonedStatusChange(event) {
      if (!this.pendingStatus || this.trimmedReason.length === 0) {
        event.preventDefault()

        return
      }

      this.updateStatus(this.pendingStatus, this.trimmedReason)
    },
    resetReasonModal() {
      this.reason = ''
      this.pendingStatus = null
      this.reasonModalType = 'default'
    },
    requiresReason(transition) {
      return !!this.getRequiredInput(transition, 'reason')
    },
    resolveReasonModalType(transition) {
      const requiredInput = this.getRequiredInput(transition, 'reason')
      const mode = requiredInput?.metadata?.reasonRequirementMode

      return mode === 'delay' ? 'delay' : 'default'
    },
    getRequiredInput(transition, inputName) {
      const requiredInputs = Array.isArray(transition?.requiredInputs) ? transition.requiredInputs : []

      return requiredInputs.find(requiredInput => requiredInput?.name === inputName) || null
    },
  },
}
</script>

<style lang="scss" scoped>
.status-change-master-warning {
  background-color: #e9ecef;
  border-color: #ced4da;
  color: #495057;
}
</style>
